/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import moment from 'moment';
import LineChart from '@ion/charting/src/line/LineChart';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import CurrentSensorStatus from './CurrentSensorStatus';

export interface LineChartDisplayProps {
  noDataLabel: string;
  data: {
    id: string,
    data: any[];
    threshold?: {
      max: number,
      min: number
    }
  }
  loading?: boolean;
  xScale?: {[key: string]: any};
  yScale?: {[key: string]: any};
  axisBottom?: {[key: string]: any};
  axisLeft?: {[key: string]: any};
  customToolTip?: Function;
  enableArea?: boolean;
  enableSlices?: false | 'x' | 'y';
  includeLegends?: boolean;
  containTooltip?: boolean;
}

/**
 * LineChartDisplay is a component used for sidesheet charts display.
 *
 * ```typescript
 *  import {LineChartDisplay} from '@/ion-suite/src/routes/tracking/scDetails/reports';
 *
 *   <LineChartDisplay noDataLabel,
 *     data={{
 *       id   : 'Temperature',
 *       data : getDataForRange(queryData?.reports?.accumulatedResults?.temperature?.data, range)
 *     }}
 *     loading={isLoading},
 *     xScale={xScale}
 *     yScale={yScale}
 *     axisBottom={axisBottom}
 *     axisLeft={axisLeft}
 *     customToolTip={ChartsTooltip} />
 * ```
 */
export const LineChartDisplay = ({
  noDataLabel,
  data,
  loading,
  customToolTip,
  includeLegends,
  enableArea = true,
  enableSlices = false,
  containTooltip = false
}: LineChartDisplayProps) => {
  const preferredTimeFormat = UserPreferences.getSystemTimeFormat();
  const isCurrent            = isCurrentTheme();
  // current theme recolors the chart's axis legend/domain lines to the
  // Asset Details Drawer's Inter/grey3-grey7 palette - the legacy theme's
  // own legendTheme/domainTheme values (Roboto-era grey3/grey4) are kept
  // byte-for-byte below and only swapped out when current ...
  const legendTheme = useMemo(() => (isCurrent ? {
    legend : {
      text : {
        fontFamily : 'Inter',
        fontSize   : 10,
        color      : dlsColor('grey3')
      }
    }
  } : {
    legend : {
      text : {
        fontSize : 14,
        color    : dlsColor('grey3')
      }
    }
  }), [isCurrent]);
  const domainTheme = useMemo(() => (isCurrent ? {
    domain : {
      line : {
        stroke      : dlsColor('grey7'),
        strokeWidth : 1
      }
    }
  } : {
    domain : {
      line : {
        stroke      : dlsColor('grey4'),
        strokeWidth : 1
      }
    }
  }), [isCurrent]);
  const yScale = useMemo(() => ({
    type    : 'linear',
    min     : 'auto',
    max     : 'auto',
    stacked : false,
    reverse : false
  }), []);

  const axisLeft = useMemo(() => {
    return {
      tickSize     : 10,
      tickPadding  : 10,
      tickRotation : 0,
      tickValues   : 10
    };
  }, []);

  const xScale = useMemo(() => ({
    type      : 'time',
    format    : '%Y-%m-%dT%H:%M:%S.%L%Z',
    precision : 'minute'
  }), []);

  const axisBottom = useMemo(() => ({
    tickValues  : 'every 2 hour',
    tickSize    : 10,
    format      : (data) => moment(data).format(preferredTimeFormat),
    tickPadding : 0
  }), [data]);

  const getPointSymbol = useCallback(function(e) {
    const {max, min} = e?.datum?.threshold || data.threshold;
    const {y: value} = e.datum;
    let colorFill = dlsColor('caBlue');
    if ((value > max) || (value < min)) {
      colorFill =  dlsColor('caRed');
    }
    return (
      <circle cx="0" cy="0" r="5" fill={colorFill} />
    );
  }, [data]);
  return (
    <>
      <CurrentSensorStatus data={data} includeLegends={includeLegends}/>
      <LineChart containTooltip={containTooltip}
        noDataLabel={noDataLabel}
        enableSlices={enableSlices}
        useMesh={true}
        animate={true}
        loading={loading}
        pointBorderWidth={0}
        getColor={dlsColor('aaBlue')}
        pointSymbol={getPointSymbol}
        enableArea={enableArea}
        isInteractive={true}
        lineWidth={2}
        pointSize={9}
        enableGridX={false}
        axisTop={null}
        axisRight={null}
        curve={'monotoneY'}
        xScale={xScale as any}
        yScale={yScale as any}
        axisBottom={axisBottom as any}
        axisLeft={axisLeft as any}
        data={[data]}
        margin={{top: 30, right: 30, bottom: 60, left: 60}}
        renderTooltipLabel={customToolTip}
        legendTheme={legendTheme}
        domainTheme={domainTheme}/>
    </>
  );
};
